import { createContext, useContext, useState, useEffect, useCallback } from "react";

const ThemeContext = createContext();

const THEME_STORAGE_KEY = "preferred-theme";
const SYSTEM_THEME_QUERY = "(prefers-color-scheme: dark)";

const parseBooleanEnv = (value) => {
  if (typeof value !== "string") {
    return false;
  }

  return ["true", "1", "yes", "on"].includes(value.trim().toLowerCase());
};

const DARK_MODE_ENABLED = parseBooleanEnv(process.env.REACT_APP_DARK_MODE_ENABLED);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (!DARK_MODE_ENABLED) {
      return "light";
    }

    // Intentar cargar tema desde localStorage
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme === "light" || savedTheme === "dark") {
      return savedTheme;
    }

    // Por defecto arrancamos en oscuro para garantizar activación inmediata.
    return "dark";
  });

  const toggleTheme = useCallback(() => {
    if (!DARK_MODE_ENABLED) {
      setTheme("light");
      localStorage.setItem(THEME_STORAGE_KEY, "light");
      return;
    }

    setTheme((prevTheme) => {
      const newTheme = prevTheme === "light" ? "dark" : "light";
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
      return newTheme;
    });
  }, []);

  const setLightTheme = useCallback(() => {
    setTheme("light");
    localStorage.setItem(THEME_STORAGE_KEY, "light");
  }, []);

  const setDarkTheme = useCallback(() => {
    if (!DARK_MODE_ENABLED) {
      setTheme("light");
      localStorage.setItem(THEME_STORAGE_KEY, "light");
      return;
    }

    setTheme("dark");
    localStorage.setItem(THEME_STORAGE_KEY, "dark");
  }, []);

  // Aplicar tema al documento
  useEffect(() => {
    if (!DARK_MODE_ENABLED && theme !== "light") {
      setTheme("light");
      localStorage.setItem(THEME_STORAGE_KEY, "light");
      return;
    }

    const root = document.documentElement;

    // Remover clases de tema anteriores
    root.classList.remove("theme-light", "theme-dark");

    // Añadir nueva clase de tema
    root.classList.add(`theme-${theme}`);

    // También aplicar como data attribute para mayor flexibilidad
    root.setAttribute("data-theme", theme);
  }, [theme]);

  // Escuchar cambios en preferencia del sistema (solo si no hay preferencia guardada)
  useEffect(() => {
    if (!DARK_MODE_ENABLED) {
      return;
    }

    if (localStorage.getItem(THEME_STORAGE_KEY)) {
      return; // Usuario tiene preferencia guardada, no seguir preferencia del sistema
    }

    const mediaQuery = window.matchMedia(SYSTEM_THEME_QUERY);
    const handleChange = (e) => {
      setTheme(e.matches ? "dark" : "light");
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      // Soporte para navegadores más antiguos
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  const value = {
    theme,
    isDark: theme === "dark",
    isLight: theme === "light",
    isDarkModeEnabled: DARK_MODE_ENABLED,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme debe usarse dentro de ThemeProvider");
  }
  return context;
};

export { ThemeContext };
